import { useRef } from "react";
import { MapPin, Menu, Phone } from "lucide-react";
import { Link, useLocation } from "wouter";
import { SITE_DETAILS } from "@shared/site";

const navigation = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Vitality Program", href: "/vitality-class", accent: true },
  { label: "Our Team", href: "/our-team" },
  { label: "Resources", href: "/patient-resources" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export function SiteHeader() {
  const [location] = useLocation();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const isActive = (href: string) => href === "/" ? location === "/" : location.startsWith(href);
  const closeMobileMenu = () => {
    if (detailsRef.current) detailsRef.current.open = false;
  };

  return (
    <>
      <div className="utility-bar">
        <div className="container utility-bar-inner">
          <a
            className="utility-address"
            href={SITE_DETAILS.directionsUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`Get directions to ${SITE_DETAILS.addressLine1}, ${SITE_DETAILS.addressLocality}`}
          >
            <MapPin size={14} aria-hidden="true" />
            <span>{SITE_DETAILS.addressLine1}, {SITE_DETAILS.addressLocality}</span>
          </a>
          <div className="utility-meta">
            <span>{SITE_DETAILS.hoursSummary}</span>
            <a href={SITE_DETAILS.phoneHref}>{SITE_DETAILS.phoneDisplay}</a>
          </div>
        </div>
      </div>

      <header className="site-header">
        <div className="container site-header-inner">
          <Link href="/" className="brand-link" aria-label="CORE Strategies Physical Therapy home">
            <img
              src="/manus-storage/core-strategies-logo_ea1e6c9d.webp"
              alt="CORE Strategies Physical Therapy"
              width="290"
              height="110"
              fetchPriority="high"
              decoding="async"
              className="brand-logo"
            />
          </Link>

          <nav className="desktop-nav" aria-label="Primary navigation">
            {navigation.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`${item.accent ? "nav-link nav-link-accent" : "nav-link"}${isActive(item.href) ? " active" : ""}`}
                aria-current={isActive(item.href) ? "page" : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <a className="header-call" href={SITE_DETAILS.phoneHref}>
            <Phone aria-hidden="true" size={17} strokeWidth={2.2} />
            <span>Call {SITE_DETAILS.phoneDisplay}</span>
          </a>

          <details className="mobile-nav" ref={detailsRef}>
            <summary aria-label="Open navigation menu">
              <Menu aria-hidden="true" size={25} />
            </summary>
            <nav aria-label="Mobile navigation">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`${item.accent ? "mobile-link accent" : "mobile-link"}${isActive(item.href) ? " active" : ""}`}
                  aria-current={isActive(item.href) ? "page" : undefined}
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </Link>
              ))}
              <a className="mobile-call" href={SITE_DETAILS.phoneHref}>
                <Phone aria-hidden="true" size={18} />
                {SITE_DETAILS.phoneDisplay}
              </a>
            </nav>
          </details>
        </div>
      </header>
    </>
  );
}
